import {
  convertQuoteToRoute,
  createConfig,
  EVM,
  executeRoute,
  getQuote,
  LiFiStep,
  Solana,
} from '@lifi/sdk'
import i18next from 'i18next'
import { config } from '../../appConfig'
import { SWAP_COMMISSION } from '../../constants/business'
import { UNKNOWN_ERROR } from '../../constants/swapErrors'
import { SOL_ID } from '../../constants/tokenIds'
import { EVMChain, PlugToken } from '../../interfaces/common'
import {
  EarnChain,
  EarnPosition,
  EarnProtocol,
  EarnVault,
  ListVaultsParams,
  PaginatedResponse,
} from '../../interfaces/earn'
import {
  SwapProvider,
  SwapProviderQuote,
  SwapProviderQuoteFetchError,
} from '../../interfaces/swapInterface'
import { Wallet } from '../../providers/WalletProvider'
import { getWalletAddress } from '../../utils/getWalletAddress'
import { getEvmProviderOptions } from './getEvmProviderOptions'
import { getSolanaProviderOptions } from './getSolanaProviderOptions'

export const LIFI_COMMISSION = 0.0025
const LIFI_SOLANA_CHAIN_ID = 1151111081099710
const EARN_BASE_URL = 'https://earn.li.fi/v1'

const liFiEvmProvider = EVM()
const lifiSolanaProvider = Solana()

createConfig({
  integrator: 'plugcommission',
  apiKey: config.fetchEnv('LIFI_API_KEY'),
  providers: [liFiEvmProvider, lifiSolanaProvider],
  rpcUrls: {
    [LIFI_SOLANA_CHAIN_ID]: [config.fetchEnv('SOLANA_RPC_ENDPOINT')],
  },
  routeOptions: {
    fee: SWAP_COMMISSION,
  },
})

class LiFiClient {
  async getQuote(
    amount: bigint,
    fromToken: PlugToken,
    toToken: PlugToken,
    wallet: Wallet,
    slippage: number
  ): Promise<SwapProviderQuote | SwapProviderQuoteFetchError> {
    const fromAddress = getWalletAddress(fromToken, wallet)
    const toAddress = getWalletAddress(toToken, wallet)

    if (!fromAddress || !toAddress) {
      return {
        provider: SwapProvider.LIFI,
        error: 'Unsupported token',
      }
    }

    try {
      const response = await getQuote({
        fromChain: fromToken.chain,
        toChain: toToken.chain,
        fromToken: this.#normalizeAddress(fromToken),
        toToken: this.#normalizeAddress(toToken),
        fromAmount: amount.toString(),
        slippage: (slippage / 100).toString(),
        allowDestinationCall: true,
        fromAddress,
        toAddress,
      })

      if (response.estimate?.toAmount) {
        return {
          price: Number(response.estimate.toAmount),
          priceHuman: Number(response.estimate.toAmount) * 10 ** -toToken.decimals,
          provider: SwapProvider.LIFI,
          metadata: {
            lifiQuote: response,
          },
        }
      } else {
        return { error: UNKNOWN_ERROR, provider: SwapProvider.LIFI }
      }
    } catch (e) {
      if ((e as Error).message.toLowerCase().includes('invalid address')) {
        return { error: i18next.t('swap.errors.no_quote'), provider: SwapProvider.LIFI }
      } else {
        return { error: UNKNOWN_ERROR, provider: SwapProvider.LIFI }
      }
    }
  }

  async swap(quote: SwapProviderQuote, wallet: Wallet, evmChains: EVMChain[]) {
    liFiEvmProvider.setOptions(getEvmProviderOptions(wallet, evmChains))
    lifiSolanaProvider.setOptions(getSolanaProviderOptions(wallet))

    await executeRoute(convertQuoteToRoute(quote.metadata!.lifiQuote), {
      disableMessageSigning: true,
    })

    return quote.metadata!.lifiQuote.estimate.toAmount
  }

  #normalizeAddress(token: PlugToken) {
    if (token.id === SOL_ID) {
      return '11111111111111111111111111111111'
    } else if (token.address) {
      return token.address
    } else {
      throw new Error(`Token ${token.id} is not supported`)
    }
  }

  async #request<T>(baseUrl: string, path: string): Promise<T> {
    const response = await fetch(`${baseUrl}${path}`, {
      headers: {
        'x-lifi-api-key': config.fetchEnv('LIFI_API_KEY'),
      },
    })

    if (response.ok) {
      return response.json() as Promise<T>
    } else {
      const errorText = await response.text()
      throw new Error(`HTTP ${response.status} error - ${errorText}`)
    }
  }

  async getDepositQuote(vault: EarnVault, amount: bigint, wallet: Wallet) {
    if (!wallet.ethAddress) {
      throw new Error('Wallet ETH address is not available')
    }

    return getQuote({
      fromChain: vault.chainId,
      toChain: vault.chainId,
      fromToken: vault.underlyingTokens[0].address,
      toToken: vault.address,
      fromAmount: amount.toString(),
      fromAddress: wallet.ethAddress,
      toAddress: wallet.ethAddress,
      fee: 0,
    })
  }

  async executeDeposit(quote: LiFiStep, wallet: Wallet, evmChains: EVMChain[]) {
    liFiEvmProvider.setOptions(getEvmProviderOptions(wallet, evmChains))

    return executeRoute(convertQuoteToRoute(quote), { disableMessageSigning: true })
  }

  async withdraw(position: EarnPosition, amount: bigint, wallet: Wallet, evmChains: EVMChain[]) {
    if (!wallet.ethAddress) {
      throw new Error('Wallet ETH address is not available')
    }

    liFiEvmProvider.setOptions(getEvmProviderOptions(wallet, evmChains))

    const quote = await getQuote({
      fromChain: position.chainId,
      toChain: position.chainId,
      fromToken: position.address,
      toToken: position.asset.address,
      fromAmount: amount.toString(),
      fromAddress: wallet.ethAddress,
      toAddress: wallet.ethAddress,
    })

    return executeRoute(convertQuoteToRoute(quote), { disableMessageSigning: true })
  }

  listVaults(params: ListVaultsParams = {}): Promise<PaginatedResponse<EarnVault>> {
    const query = new URLSearchParams({ isComposerSupported: 'true', isTransactional: 'true' })

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        query.set(key, value.toString())
      }
    })

    return this.#request(EARN_BASE_URL, `/vaults?${query.toString()}`)
  }

  getVault(chainId: number, address: string): Promise<EarnVault> {
    return this.#request(EARN_BASE_URL, `/vaults/${chainId}/${address}`)
  }

  async getPositions(userAddress: string): Promise<EarnPosition[]> {
    const { positions } = await this.#request<{ positions: EarnPosition[] }>(
      EARN_BASE_URL,
      `/portfolio/${userAddress}/positions`
    )

    return positions
  }

  listChains(): Promise<EarnChain[]> {
    return this.#request(EARN_BASE_URL, '/chains')
  }

  listProtocols(): Promise<EarnProtocol[]> {
    return this.#request(EARN_BASE_URL, '/protocols')
  }
}

export default LiFiClient
